import type { PlanCue } from '@toolproof-core/lib/_contract/scene-plan';

import narrationConfig from '@/_data/narrationConfig.json' with { type: 'json' };
import type { RecordedNarrationClip } from '@/_contract/recordedNarration';

export function resolveRecordedNarration(
    clips: readonly RecordedNarrationClip[], key: string, text: string, catalog: string,
    cues?: readonly Pick<PlanCue, 'id' | 'beforeText'>[],
    audioBaseUrl?: string,
    narrationPauses?: readonly { offset: number; milliseconds: number }[],
    regenerationHint?: string,
): { url: string; timepoints?: RecordedNarrationClip['timepoints']; wordBoundaries?: RecordedNarrationClip['wordBoundaries']; error?: never }
    | { error: string; url?: never; timepoints?: never; wordBoundaries?: never } {
    const regenerate = regenerationHint
        ?? (audioBaseUrl ? 'Generate audition narration in the developer panel' : 'Run pnpm narration:generate');
    const clip = clips.find((entry) => entry.key === key && entry.voice === narrationConfig.production.voice);
    if (!clip || clip.text !== text || clip.pauseSignature !== (narrationPauses ? JSON.stringify(narrationPauses) : undefined)) {
        return { error: `Recorded narration is missing or stale for '${key}'. ${regenerate}, or turn sound off to continue.` };
    }
    if (cues?.length && (clip.cueSignature !== JSON.stringify(cues)
        || clip.timepoints?.length !== cues.length
        || !clip.timepoints.every((point, index) => point.markName === cues[index].id
            && Number.isFinite(point.timeSeconds)
            && (point.timeSeconds > 0 || (point.timeSeconds === 0 && index === 0 && text.indexOf(cues[0].beforeText) === 0))
            && (index === 0 || point.timeSeconds > clip.timepoints![index - 1].timeSeconds)))) {
        return { error: `Recorded narration cue timing is missing or stale for '${key}'. ${regenerate}, or turn sound off to continue.` };
    }
    const words = text.match(/\S+/g) ?? [];
    const boundaries = clip.wordBoundaries;
    const timedWords = boundaries?.length === words.length + 1 && boundaries.every((time, index) =>
        Number.isFinite(time) && time >= 0 && (index === 0 || time >= boundaries[index - 1]!));
    return { url: `${audioBaseUrl ?? `/narration/production/${catalog}/${narrationConfig.production.voice}`}/${clip.file}`,
        ...(cues?.length ? { timepoints: clip.timepoints } : {}),
        ...(timedWords ? { wordBoundaries: boundaries } : {}) };
}
